import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { AccessTime } from '@mui/icons-material';

const dayjs = require('dayjs');
const utc = require('dayjs/plugin/utc');
const timezone = require('dayjs/plugin/timezone');

dayjs.extend(utc);
dayjs.extend(timezone);

const EventDetails = ({ event, eventAsset }) => {
    const [timeUntilStart, setTimeUntilStart] = useState(0);
    const [timeUntilEnd, setTimeUntilEnd] = useState(0);
    const [isEventLive, setIsEventLive] = useState(false);

    useEffect(() => {
        const calculateTimes = () => {
            const currentTime = dayjs();

            // Calculate time until start
            if (event.valid_from) {
                const startTime = dayjs.utc(event.valid_from).local();
                const startDiffInMs = startTime.diff(currentTime);
                const newTimeUntilStart = Math.max(0, startDiffInMs);
                setTimeUntilStart(newTimeUntilStart);

                // Check if event is live
                const hasStarted = newTimeUntilStart === 0;
                setIsEventLive(hasStarted);
            }

            // Calculate time until end only if event is live
            if (event.valid_until) {
                const endTime = dayjs.utc(event.valid_until).local();
                const endDiffInMs = endTime.diff(currentTime);
                setTimeUntilEnd(Math.max(0, endDiffInMs));
            }
        };

        calculateTimes();

        const interval = setInterval(calculateTimes, 1000);

        return () => clearInterval(interval);
    }, [event.valid_from, event.valid_until]);

    const formatTime = (ms) => {
        const days = Math.floor(ms / (1000 * 60 * 60 * 24));
        const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((ms % (1000 * 60)) / 1000);

        return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    };

    const getCountdownDisplay = () => {
        if (!isEventLive && timeUntilStart > 0) {
            return `Available in ${formatTime(timeUntilStart)}`;
        } else if (isEventLive && timeUntilEnd > 0) {
            return `Ends in ${formatTime(timeUntilEnd)}`;
        } else if (timeUntilEnd === 0) {
            return 'Event has ended';
        }
        return null;
    };

    const showCountdown = ((!isEventLive && timeUntilStart > 0) || (isEventLive && timeUntilEnd > 0));

    return (
        <>
            <Box sx={{ mb: 4, position: 'relative', width: '100%', paddingTop: '56.25%', overflow: 'hidden' }}>
                {/* Single Banner Image */}
                <Box
                    component="img"
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain'
                    }}
                    src={eventAsset[0]?.assets.bannerUrl}
                    alt={event.name}
                />

                {/* Countdown Timer */}
                {showCountdown && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        padding: 2,
                        background: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        zIndex: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            {getCountdownDisplay()}
                        </Typography>
                    </Box>
                )}
            </Box>

            <Box sx={{ px: 3 }}>
                <Typography variant="h4" gutterBottom>{event.name}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AccessTime sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                        {dayjs.utc(event.valid_from).local().format('dddd, MMMM D · h:mm A')}
                        {event.valid_until && (
                            <> - {dayjs.utc(event.valid_until).local().format('dddd, MMMM D · h:mm A')}</>
                        )}
                    </Typography>
                </Box>
            </Box>
        </>
    );
};

export default EventDetails;